import {
    IDefrostAlarmSensor,
    IEmptyMixerAlarmSensor,
    IInterruptionAlarmSensor,
    ILowLoadAlarmSensor,
    INoDataAlarmSensor,
    IOutsideRegularTimeAlarmSensor,
    IPeakHourAlarmSensor,
    IPowerFactorAlarmSensor,
    IDoorOpenAlarmSensor,
    ICriticalTemperatureAlarmSensor,
    IOvervoltageAlarmSensor
} from '@/interfaces/entities/alarm_profile';
import AlarmProfileResource from '@/api/resources/alarmProfile';
import { defineStore } from 'pinia';
import { clearAllStores } from '@/services/clear-stores';
import { useSuccessStore } from './success';
import { useErrorStore } from './error';
import { ValidationError } from 'yup';


export const useAlarmProfileStore = defineStore({
    id: 'alarmProfile',

    state: () => ({
        interruptionProfiles: [] as IInterruptionAlarmSensor[],
        lowLoadProfiles: [] as ILowLoadAlarmSensor[],
        peakHourProfiles: [] as IPeakHourAlarmSensor[],
        defrostTimeProfiles: [] as IDefrostAlarmSensor[],
        emptyMixerProfiles: [] as IEmptyMixerAlarmSensor[],
        outsideRegularTimeProfiles: [] as IOutsideRegularTimeAlarmSensor[],
        powerFactorProfiles: [] as IPowerFactorAlarmSensor[],
        noDataProfiles: [] as INoDataAlarmSensor[],
        doorOpenProfiles: [] as IDoorOpenAlarmSensor[],
        criticalTemperatureProfiles: [] as ICriticalTemperatureAlarmSensor[],
        overvoltageProfiles: [] as IOvervoltageAlarmSensor[],
    }),

    getters: {
        listInterruptionProfiles: (state) => state.interruptionProfiles,
        listLowLoadProfiles: (state) => state.lowLoadProfiles,
        listPeakHourProfiles: (state) => state.peakHourProfiles,
        listDefrostTimeProfiles: (state) => state.defrostTimeProfiles,
        listEmptyMixerProfiles: (state) => state.emptyMixerProfiles,
        listOutsideRegularTimeProfiles: (state) => state.outsideRegularTimeProfiles,
        listPowerFactorProfiles: (state) => state.powerFactorProfiles,
        listNoDataProfiles: (state) => state.noDataProfiles,
        listDoorOpenProfiles: (state) => state.doorOpenProfiles,
        listCriticalTemperatureProfiles: (state) => state.criticalTemperatureProfiles,
        listOvervoltageProfiles: (state) => state.overvoltageProfiles,
    },

    actions: {

        clearAllStores() {
            clearAllStores();
        },

        async fetchInterruptionAlarmProfiles({ force = false }) {
            if (this.interruptionProfiles.length === 0 || force) {
                try {
                    this.interruptionProfiles = await AlarmProfileResource.getAllInterruptionProfiles()

                } catch {
                    // Erro ao buscar perfis de interrupção
                }
            }
        },

        async fetchLowLoadAlarmProfiles({ force = false }) {
            if (this.lowLoadProfiles.length === 0 || force) {
                try {
                    this.lowLoadProfiles = await AlarmProfileResource.getAllLowLoadProfiles()

                } catch {
                    // Erro ao buscar perfis de baixa carga
                }
            }
        },

        async fetchPeakHourAlarmProfiles({ force = false }) {
            if (this.peakHourProfiles.length === 0 || force) {
                try {
                    this.peakHourProfiles = await AlarmProfileResource.getAllPeakHourProfiles()

                } catch {
                    // Erro ao buscar perfis de hora de pico
                }
            }
        },

        async fetchDefrostTimeAlarmProfiles({ force = false }) {
            if (this.defrostTimeProfiles.length === 0 || force) {
                try {
                    this.defrostTimeProfiles = await AlarmProfileResource.getAllDefrostTimeProfiles()

                } catch {
                    // Erro ao buscar perfis de degelo
                }
            }
        },

        async fetchEmptyMixerAlarmProfiles({ force = false }) {

            if (this.emptyMixerProfiles.length === 0 || force) {
                try {
                    this.emptyMixerProfiles = await AlarmProfileResource.getAllEmptyMixerProfiles()

                } catch {
                    // Erro ao buscar perfis de misturador vazio
                }
            }
        },

        async fetchOutsideRegularTimeAlarmProfiles({ force = false }) {
            if (this.outsideRegularTimeProfiles.length === 0 || force) {
                try {
                    this.outsideRegularTimeProfiles = await AlarmProfileResource.getAllOutsideRegularTimeProfiles()
                } catch {
                    // Erro ao buscar perfis fora do horário regular
                }
            }
        },

        async fetchPowerFactorAlarmProfiles({ force = false }) {
            if (this.powerFactorProfiles.length === 0 || force) {
                try {
                    this.powerFactorProfiles = await AlarmProfileResource.getAllPowerFactorProfiles()

                } catch {
                    // Erro ao buscar perfis de fator de potência
                }
            }
        },

        async fetchNoDataAlarmProfiles({ force = false }) {
            if (this.noDataProfiles.length === 0 || force) {
                try {
                    this.noDataProfiles = await AlarmProfileResource.getAllNoDataProfiles()

                } catch {
                    // Erro ao buscar perfis de ausência de dados
                }
            }
        },

        async fetchDoorOpenAlarmProfiles({ force = false }) {
            if (this.doorOpenProfiles.length === 0 || force) {
                try {
                    this.doorOpenProfiles = await AlarmProfileResource.getAllDoorOpenProfiles()

                } catch {
                    // Erro ao buscar perfis de porta aberta
                }
            }
        },

        async fetchCriticalTemperatureAlarmProfiles({ force = false }) {
            if (this.criticalTemperatureProfiles.length === 0 || force) {
                try {
                    this.criticalTemperatureProfiles = await AlarmProfileResource.getAllCriticalTemperatureProfiles()

                } catch {
                    // Erro ao buscar perfis de temperatura crítica
                }
            }
        },

        async fetchOvervoltageAlarmProfiles({ force = false }) {
            if (this.overvoltageProfiles.length === 0 || force) {
                try {
                    this.overvoltageProfiles = await AlarmProfileResource.getAllOvervoltageProfiles()

                } catch {
                    // Erro ao buscar perfis de sobretensão
                }
            }
        },
        
        //create functions

        async createNoDataAlarmProfile(newProfile: INoDataAlarmSensor) {
            try {
                const createdNoDataProfile = await AlarmProfileResource.createNoDataAlarmProfile(newProfile)
                this.noDataProfiles.push(createdNoDataProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Ausência de Dados criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createInterruptionAlarmProfile(newProfile: IInterruptionAlarmSensor) {
            try {
                const createdInterruptionProfile = await AlarmProfileResource.createInterruptionAlarmProfile(newProfile)
                this.interruptionProfiles.push(createdInterruptionProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Interrupção criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createLowLoadAlarmProfile(newProfile: ILowLoadAlarmSensor) {
            try {
                const createdLowLoadProfile = await AlarmProfileResource.createLowLoadAlarmProfile(newProfile)
                this.lowLoadProfiles.push(createdLowLoadProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Baixa Carga criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createPeakHourAlarmProfile(newProfile: IPeakHourAlarmSensor) {
            try {
                const createdPeakHourProfile = await AlarmProfileResource.createPeakHourAlarmProfile(newProfile)
                this.peakHourProfiles.push(createdPeakHourProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Hora de Pico criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createDefrostTimeAlarmProfile(newProfile: IDefrostAlarmSensor) {
            try {
                const createdDefrostTimeProfile = await AlarmProfileResource.createDefrostTimeAlarmProfile(newProfile)
                this.defrostTimeProfiles.push(createdDefrostTimeProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Tempo de Degelo criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createEmptyMixerAlarmProfile(newProfile: IEmptyMixerAlarmSensor) {
            try {
                const createdEmptyMixerProfile = await AlarmProfileResource.createEmptyMixerAlarmProfile(newProfile)
                this.emptyMixerProfiles.push(createdEmptyMixerProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Misturador Vazio criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createOutsideRegularTimeAlarmProfile(newProfile: IOutsideRegularTimeAlarmSensor) {
            try {
                const createdOutsideRegularTimeProfile = await AlarmProfileResource.createOutsideRegularTimeAlarmProfile(newProfile)
                this.outsideRegularTimeProfiles.push(createdOutsideRegularTimeProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Fora do Horário Regular criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createPowerFactorAlarmProfile(newProfile: IPowerFactorAlarmSensor) {
            try {
                const createdPowerFactorProfile = await AlarmProfileResource.createPowerFactorAlarmProfile(newProfile)
                this.powerFactorProfiles.push(createdPowerFactorProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Fator de Potência criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createCriticalTemperatureAlarmProfile(newProfile: ICriticalTemperatureAlarmSensor) {
            try {
                const createdCriticalTemperatureProfile = await AlarmProfileResource.createCriticalTemperatureAlarmProfile(newProfile)
                this.criticalTemperatureProfiles.push(createdCriticalTemperatureProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Temperatura Crítica criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createDoorOpenAlarmProfile(newProfile: IDoorOpenAlarmSensor) {
            try {
                const createdDoorOpenProfile = await AlarmProfileResource.createDoorOpenAlarmProfile(newProfile)
                this.doorOpenProfiles.push(createdDoorOpenProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Porta Aberta criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        async createOvervoltageAlarmProfile(newProfile: IOvervoltageAlarmSensor) {
            try {
                const createdOvervoltageProfile = await AlarmProfileResource.createOvervoltageAlarmProfile(newProfile)
                this.overvoltageProfiles.push(createdOvervoltageProfile)
                useSuccessStore().setSuccess(['Perfil de Alarme de Sobretensão criado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao criar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao criar perfil: ', [error?.response?.data?.errors || 'Falha ao criar perfil'])
            }
        },

        //edit functions

        async editNoDataAlarmProfile(profile_id: number, editProfile: INoDataAlarmSensor) {
            try {
                const editedNoDataProfile = await AlarmProfileResource.editNoDataAlarmProfile(profile_id, editProfile)
                const index = this.noDataProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.noDataProfiles[index] = editedNoDataProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Ausência de Dados editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editInterruptionAlarmProfile(profile_id: number, editProfile: IInterruptionAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editInterruptionAlarmProfile(profile_id, editProfile)
                const index = this.interruptionProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.interruptionProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Interrupção editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editLowLoadAlarmProfile(profile_id: number, editProfile: ILowLoadAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editLowLoadAlarmProfile(profile_id, editProfile)
                const index = this.lowLoadProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.lowLoadProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Baixa Carga editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editPeakHourAlarmProfile(profile_id: number, editProfile: IPeakHourAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editPeakHourAlarmProfile(profile_id, editProfile)
                const index = this.peakHourProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.peakHourProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Hora de Pico editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editDefrostTimeAlarmProfile(profile_id: number, editProfile: IDefrostAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editDefrostTimeAlarmProfile(profile_id, editProfile)
                const index = this.defrostTimeProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.defrostTimeProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Tempo de Degelo editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editEmptyMixerAlarmProfile(profile_id: number, editProfile: IEmptyMixerAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editEmptyMixerAlarmProfile(profile_id, editProfile)
                const index = this.emptyMixerProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.emptyMixerProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Misturador Vazio editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editOutsideRegularTimeAlarmProfile(profile_id: number, editProfile: IOutsideRegularTimeAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editOutsideRegularTimeAlarmProfile(profile_id, editProfile)
                const index = this.outsideRegularTimeProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.outsideRegularTimeProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Fora do Horário Regular editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editPowerFactorAlarmProfile(profile_id: number, editProfile: IPowerFactorAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editPowerFactorAlarmProfile(profile_id, editProfile)
                const index = this.powerFactorProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.powerFactorProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Fator de Potência editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editCriticalTemperatureAlarmProfile(profile_id: number, editProfile: ICriticalTemperatureAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editCriticalTemperatureAlarmProfile(profile_id, editProfile)
                const index = this.criticalTemperatureProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.criticalTemperatureProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Temperatura Crítica editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editDoorOpenAlarmProfile(profile_id: number, editProfile: IDoorOpenAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editDoorOpenAlarmProfile(profile_id, editProfile)
                const index = this.doorOpenProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.doorOpenProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Porta Aberta editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

        async editOvervoltageAlarmProfile(profile_id: number, editProfile: IOvervoltageAlarmSensor) {
            try {
                const editedProfile = await AlarmProfileResource.editOvervoltageAlarmProfile(profile_id, editProfile)
                const index = this.overvoltageProfiles.findIndex(profile => profile.id === profile_id);
                if (index !== -1) {
                    this.overvoltageProfiles[index] = editedProfile;
                }
                useSuccessStore().setSuccess(['Perfil de Alarme de Sobretensão editado com sucesso!'])
            } catch (error) {
                if (error instanceof ValidationError){
                    useErrorStore().setErrors('Erro ao editar perfil: ', error.errors)
                }
                useErrorStore().setErrors('Erro ao editar perfil: ', [error?.response?.data?.errors || 'Falha ao editar perfil'])
            }
        },

    },
});

